// src/pages/NotaireDashboard.jsx
// Dashboard notaire prescripteur (§22.5 stratégie notaires). LECTURE des dossiers
// art partagés avec l'étude via collection_permissions — consomme les données
// existantes (aucune ressaisie). Accès par PERMISSION (RPC notaire_dossiers,
// grantee = utilisateur courant). Complétude calculée sur la présence réelle.
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";

function Bar({ pct }) {
  const tone = pct >= 70 ? "bg-emerald-500" : pct >= 40 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
      <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.max(2, pct)}%` }} />
    </div>
  );
}

export default function NotaireDashboard() {
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Inviter un client (§11) : l'accès n'est créé qu'à l'acceptation par le client.
  const [inv, setInv] = useState({ email: "", name: "" });
  const [invBusy, setInvBusy] = useState(false);
  const [invMsg, setInvMsg] = useState(null);

  const invite = async (e) => {
    e.preventDefault();
    setInvBusy(true); setInvMsg(null);
    try {
      const { data, error: err } = await supabase.functions.invoke("notaire-invite", {
        body: { client_email: inv.email.trim().toLowerCase(), client_name: inv.name.trim() },
      });
      if (err || !(data && data.ok)) throw err || new Error("invite_failed");
      setInvMsg({ ok: true, text: "Invitation envoyée. Le client recevra un email pour créer son espace et vous accorder l'accès." });
      setInv({ email: "", name: "" });
    } catch (e2) {
      console.error("[notaire] invite", e2);
      setInvMsg({ ok: false, text: "L'invitation n'a pas pu être envoyée. Vérifiez l'email et réessayez." });
    } finally { setInvBusy(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase.rpc("notaire_dossiers");
      if (err) throw err;
      setDossiers(data || []);
    } catch (e) {
      console.error("[notaire] dossiers:", e);
      setError("Impossible de charger vos dossiers pour le moment.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalOeuvres = dossiers.reduce((s, d) => s + (d.nb_oeuvres || 0), 0);
  const totalManquants = dossiers.reduce((s, d) => s + (d.docs_manquants || 0), 0);
  const completudeMoy = dossiers.length
    ? Math.round(dossiers.reduce((s, d) => s + (d.completude_pct || 0), 0) / dossiers.length)
    : 0;

  return (
    <div className="min-h-screen bg-[#f6f6f4] text-gray-900" style={{ fontFamily: "Georgia, serif" }}>
      <header className="sticky top-0 z-20 border-b border-gray-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-950">Étude notariale — dossiers art</h1>
            <p className="text-sm text-gray-500 mt-0.5">Préparer. Structurer. Faire valider. Transmettre.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} disabled={loading}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60">
              {loading ? "Actualisation…" : "Actualiser"}
            </button>
            <Link to="/" className="rounded-lg bg-gray-950 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800">
              Retour au site
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-8">
        <section className="mb-8 grid gap-4 rounded-2xl border border-[#ded8ca] bg-[#fdfbf6] p-6 md:grid-cols-[1fr_auto] md:items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#8a7650]">Partenariat ARTKIV</p>
            <h2 className="mt-2 text-xl font-bold text-gray-950">Compte Notaire Prescripteur — accès gracieux</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600">
              Votre étude peut inviter ses clients et suivre les dossiers explicitement partagés. Les dossiers opérationnels sont activés et facturés séparément au collectionneur, à la famille, à la succession ou à son mandataire.
            </p>
          </div>
          <Link to="/contact" className="whitespace-nowrap rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-800 hover:border-gray-900">
            Contacter ARTKIV
          </Link>
        </section>

        {/* Synthèse */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            ["Dossiers", dossiers.length, "partagés avec l'étude"],
            ["Œuvres", totalOeuvres.toLocaleString("fr-FR"), "au total"],
            ["Complétude moyenne", `${completudeMoy}%`, "des dossiers"],
            ["Docs à réunir", totalManquants, "œuvres sans facture/certificat"],
          ].map(([label, value, helper]) => (
            <div key={label} className="rounded-2xl border border-gray-200 bg-white p-5">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
              <div className="mt-2 text-3xl font-bold text-gray-950">{loading ? "…" : value}</div>
              <div className="mt-1 text-xs text-gray-500">{helper}</div>
            </div>
          ))}
        </section>

        {/* Inviter un client */}
        <section className="mb-8 rounded-2xl border border-gray-200 bg-white p-6">
          <h2 className="text-lg font-bold text-gray-950 mb-1">Inviter un client</h2>
          <p className="text-sm text-gray-500 mb-4">
            Le client reçoit un email pour créer son espace. L'accès n'est accordé qu'après <strong>son</strong> acceptation explicite.
          </p>
          <form onSubmit={invite} className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
            <input type="email" required value={inv.email} onChange={(e) => setInv((s) => ({ ...s, email: e.target.value }))}
              placeholder="Email du client *" className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50" />
            <input value={inv.name} onChange={(e) => setInv((s) => ({ ...s, name: e.target.value }))}
              placeholder="Nom (optionnel)" className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50" />
            <button type="submit" disabled={invBusy}
              className="px-5 py-2.5 bg-gray-950 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-60 whitespace-nowrap">
              {invBusy ? "Envoi…" : "Envoyer l'invitation"}
            </button>
          </form>
          {invMsg && (
            <p className={`mt-3 text-sm ${invMsg.ok ? "text-emerald-700" : "text-rose-700"}`}>{invMsg.text}</p>
          )}
        </section>

        {error && (
          <div className="mb-6 rounded-xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700">{error}</div>
        )}

        {!loading && !error && dossiers.length === 0 && (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center">
            <h2 className="text-lg font-bold text-gray-900 mb-2">Aucun dossier partagé pour l'instant</h2>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              Un dossier apparaît ici lorsqu'un client vous accorde l'accès à sa collection.
              Le titre ou le rôle ne donne jamais accès automatiquement : l'accès résulte toujours d'une autorisation.
            </p>
          </div>
        )}

        {/* Liste des dossiers */}
        <section className="space-y-4">
          {dossiers.map((d) => (
            <div key={d.client_id} className="rounded-2xl border border-gray-200 bg-white p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="text-lg font-bold text-gray-950 truncate">{d.client_nom}</h3>
                  {d.client_email && <p className="text-sm text-gray-500 truncate">{d.client_email}</p>}
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-950">{d.nb_oeuvres}</div>
                  <div className="text-xs text-gray-500">œuvres</div>
                </div>
              </div>

              <div className="mt-4">
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="text-gray-600">Complétude du dossier</span>
                  <span className="font-semibold text-gray-900">{d.completude_pct}%</span>
                </div>
                <Bar pct={d.completude_pct} />
              </div>

              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                {[
                  ["Photographies", d.avec_photo],
                  ["Factures", d.avec_facture],
                  ["Certificats", d.avec_certificat],
                  ["Provenance", d.avec_provenance],
                ].map(([label, n]) => (
                  <div key={label} className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
                    <div className="text-gray-900 font-semibold">{n}<span className="text-gray-400 font-normal"> / {d.nb_oeuvres}</span></div>
                    <div className="text-xs text-gray-500">{label}</div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                {d.docs_manquants > 0 ? (
                  <div className="inline-flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-1.5 text-sm text-amber-800">
                    <span>⚠</span>
                    <span><strong>{d.docs_manquants}</strong> œuvre{d.docs_manquants > 1 ? "s" : ""} sans facture ou certificat rattaché</span>
                  </div>
                ) : <span />}
                <Link
                  to={`/notaire/dossier/${d.client_id}`}
                  state={{ nom: d.client_nom }}
                  className="text-sm font-medium text-gray-900 underline underline-offset-4 hover:text-gray-600 whitespace-nowrap"
                >
                  Voir le détail →
                </Link>
              </div>
            </div>
          ))}
        </section>

        <p className="mt-8 text-xs text-gray-400 max-w-2xl">
          ARTKIV prépare et structure les informations. Le notaire organise juridiquement la transmission ;
          le commissaire-priseur ou l'expert mandaté contrôle et valide les éléments relevant de sa compétence.
          Les valeurs indicatives ne constituent pas une expertise.
        </p>
      </main>
    </div>
  );
}
